import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MedicoService, FirmaResponse } from "app/services/medico.service";
import { NotificationComponent } from "../notification/notification.component";
import { FirmaCanvasComponent } from "./firma-canvas.component";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

interface FirmaUploadState {
  uploading: boolean;
  error: string | null;
  success: boolean;
}

@Component({
  selector: "app-firma-upload",
  templateUrl: "./firma-upload.component.html",
  styleUrls: ["./firma-upload.component.scss"],
})
export class FirmaUploadComponent implements OnInit, OnChanges {
  @ViewChild(FirmaCanvasComponent) firmaCanvas: FirmaCanvasComponent;

  @Input() medicoId: number;
  @Input() signaturePath: string | null = null;

  @Output() firmaActualizada = new EventEmitter<string>();
  @Output() firmaEliminada = new EventEmitter<void>();

  // Estado del componente
  state: FirmaUploadState = {
    uploading: false,
    error: null,
    success: false,
  };

  // Vista previa
  currentSignatureUrl: SafeUrl | null = null;
  selectedFile: File | null = null;
  syncedToApi: boolean | null = null;

  // Constantes para el canvas
  readonly RECOMMENDED_WIDTH = 300;
  readonly RECOMMENDED_HEIGHT = 100;

  constructor(
    private medicoService: MedicoService,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.loadCurrentSignature();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["signaturePath"]) {
      this.loadCurrentSignature();
    }
  }

  /**
   * Carga la firma actual del médico si existe
   */
  loadCurrentSignature(): void {
    if (!this.signaturePath || !this.medicoId) {
      this.currentSignatureUrl = null;
      return;
    }
    this.medicoService.descargarFirma(this.medicoId).subscribe({
      next: (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        this.currentSignatureUrl =
          this.sanitizer.bypassSecurityTrustUrl(objectUrl);
      },
      error: () => {
        this.currentSignatureUrl = null;
      },
    });
  }

  /**
   * Sube la firma al servidor
   */
  uploadSignature(): void {
    if (!this.selectedFile || !this.medicoId) {
      return;
    }

    this.state.uploading = true;
    this.state.error = null;
    this.state.success = false;

    this.medicoService.subirFirma(this.medicoId, this.selectedFile).subscribe({
      next: (response: FirmaResponse) => {
        this.state.uploading = false;

        if (response.success) {
          this.state.success = true;
          this.signaturePath = response.signature_path;
          this.loadCurrentSignature();
          this.syncedToApi = response.synced_to_api || false;
          this.selectedFile = null;
          this.firmaActualizada.emit(response.signature_path);
          this.showNotification("Firma actualizada correctamente", "exito");
        } else {
          this.state.error = response.message;
          this.showNotification(response.message, "error");
        }
      },
      error: (error) => {
        this.state.uploading = false;
        this.state.error =
          error.error?.errors?.firma?.[0] ||
          error.error?.message ||
          "Error al subir la firma";
        this.showNotification(this.state.error, "error");
      },
    });
  }

  /**
   * Cancela la selección actual
   */
  cancelSelection(): void {
    this.selectedFile = null;
    this.state.error = null;
    this.state.success = false;
  }

  /**
   * Sube la firma pendiente del canvas (si hay dibujo).
   * Retorna Observable que completa cuando se termina (o inmediatamente si no hay firma).
   */
  uploadPendingSignature(): Observable<FirmaResponse | null> {
    const file =
      this.firmaCanvas && this.firmaCanvas.hasFirma
        ? this.firmaCanvas.getFile()
        : null;

    if (!file || !this.medicoId) {
      return of(null);
    }

    this.state.uploading = true;
    this.state.error = null;
    this.state.success = false;

    return this.medicoService.subirFirma(this.medicoId, file).pipe(
      tap({
        next: (response: FirmaResponse) => {
          this.state.uploading = false;
          if (response.success) {
            this.state.success = true;
            this.signaturePath = response.signature_path;
            this.loadCurrentSignature();
            this.syncedToApi = response.synced_to_api || false;
            this.selectedFile = null;
            this.firmaActualizada.emit(response.signature_path);
          } else {
            this.state.error = response.message;
          }
        },
        error: (error) => {
          this.state.uploading = false;
          this.state.error =
            error.error?.errors?.firma?.[0] ||
            error.error?.message ||
            "Error al subir la firma";
        },
      }),
    );
  }

  /**
   * Maneja firma generada desde el canvas
   */
  onFirmaFromCanvas(file: File): void {
    this.selectedFile = file;
    this.uploadSignature();
  }

  /**
   * Limpia el estado cuando se borra el canvas
   */
  onCanvasBorrado(): void {
    this.selectedFile = null;
  }

  /**
   * Muestra notificación
   */
  private showNotification(message: string, type: "exito" | "error"): void {
    this.snackBar.openFromComponent(NotificationComponent, {
      data: { message, action: type },
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }
}
